<script lang="ts">
	import { get } from 'svelte/store';
	import { currentUser, roolClient, theme, canvasInstance, canvasEditor, gridStyle, gridSnapping } from '../../stores';
	import type { GridStyle } from '@rool/renderer';
	import { Moon as MoonIcon, Sun as SunIcon, Magnet as SnapIcon, SignOut as SignOutIcon } from 'phosphor-svelte';
	import SpaceMenu from './SpaceMenu.svelte';

	let showUserMenu = false;
	let userMenuElement: HTMLElement | null = null;

	type GridOption = { value: GridStyle; label: string };
	const gridOptions: GridOption[] = [
		{ value: 'dither', label: 'Dither' },
		{ value: 'dots', label: 'Dots' },
		{ value: 'lines', label: 'Lines' },
		{ value: 'hex', label: 'Hex' },
		{ value: 'none', label: 'None' },
	];

	$: username = $currentUser?.name || 'User';
	$: userInitials = username
		.split(' ')
		.map((word: string) => word[0])
		.join('')
		.toUpperCase();

	function setGridStyle(style: GridStyle) {
		gridStyle.set(style);
		const canvas = get(canvasInstance);
		if (canvas) {
			canvas.setGridStyle(style);
		}
		const client = get(roolClient);
		if (client) {
			client.setUserStorage('gridStyle', style);
		}
	}

	function toggleTheme() {
		theme.update((current: 'light' | 'dark') => {
			const next = current === 'light' ? 'dark' : 'light';
			localStorage.setItem('theme', next);
			const client = get(roolClient);
			if (client) {
				client.setUserStorage('theme', next);
			}
			return next;
		});
	}

	function toggleGridSnapping() {
		const next = !$gridSnapping;
		gridSnapping.set(next);
		const editor = get(canvasEditor);
		if (editor) {
			editor.setGridSnapping(next);
		}
		const client = get(roolClient);
		if (client) {
			client.setUserStorage('gridSnapping', next);
		}
	}

	function toggleUserMenu() {
		showUserMenu = !showUserMenu;
	}

	function logout() {
		const client = get(roolClient);
		if (client) {
			client.logout();
		}
		window.location.reload();
	}

	function handleClickOutside(event: MouseEvent) {
		if (showUserMenu && userMenuElement && !userMenuElement.contains(event.target as Node)) {
			showUserMenu = false;
		}
	}
</script>

<svelte:window on:click={handleClickOutside} />

<div class="topbar">
	<!-- User profile -->
	<div class="user-menu-container" bind:this={userMenuElement}>
		<button
			class="user-button"
			class:open={showUserMenu}
			type="button"
			aria-label="Account & settings"
			title={username}
			on:click={toggleUserMenu}
		>
			<span class="user-initials">{userInitials}</span>
		</button>

		{#if showUserMenu}
			<div class="user-menu">
				<!-- Account header -->
				<div class="menu-header">
					<div class="menu-header-avatar">
						<span class="menu-header-initials">{userInitials}</span>
					</div>
					<div class="menu-header-info">
						<div class="menu-header-name">{username}</div>
						{#if $currentUser}
							<div class="menu-header-meta">
								{#if $currentUser.plan}
									<span class="meta-plan">{$currentUser.plan}</span>
								{/if}
								{#if $currentUser.creditsBalance != null}
									<span class="meta-credits">{$currentUser.creditsBalance.toLocaleString()} credits</span>
								{/if}
							</div>
						{/if}
					</div>
				</div>

				<div class="menu-divider"></div>

				<!-- Appearance -->
				<div class="menu-section">
					<div class="menu-label">Appearance</div>
					<button
						class="menu-item"
						type="button"
						on:click={toggleTheme}
					>
						<span class="menu-item-icon">
							{#if $theme === 'light'}
								<MoonIcon size={16} />
							{:else}
								<SunIcon size={16} />
							{/if}
						</span>
						<span class="menu-item-text">{$theme === 'light' ? 'Dark mode' : 'Light mode'}</span>
					</button>
				</div>

				<div class="menu-divider"></div>

				<!-- Grid -->
				<div class="menu-section">
					<div class="menu-label">Grid</div>
					<button
						class="menu-item"
						class:active={$gridSnapping}
						type="button"
						on:click={toggleGridSnapping}
					>
						<span class="menu-item-icon">
							<SnapIcon size={16} />
						</span>
						<span class="menu-item-text">Snap to grid</span>
						{#if $gridSnapping}
							<span class="menu-item-check">On</span>
						{/if}
					</button>
				</div>

				<div class="menu-divider"></div>

				<div class="menu-section">
					<div class="menu-label">Grid style</div>
					<div class="grid-options">
						{#each gridOptions as option}
							<button
								class="grid-option"
								class:active={$gridStyle === option.value}
								type="button"
								on:click={() => setGridStyle(option.value)}
							>
								{option.label}
							</button>
						{/each}
					</div>
				</div>

				<div class="menu-divider"></div>

				<!-- Logout -->
				<div class="menu-section">
					<button
						class="menu-item menu-item-danger"
						type="button"
						on:click={logout}
					>
						<span class="menu-item-icon">
							<SignOutIcon size={16} />
						</span>
						<span class="menu-item-text">Log out</span>
					</button>
				</div>
			</div>
		{/if}
	</div>

	<SpaceMenu />
</div>

<style>
	.topbar {
		position: absolute;
		top: 16px;
		left: 16px;

		display: flex;
		align-items: center;
		gap: 8px;

		height: 48px;
		padding: 0 6px 0 8px;

		background: var(--panelSolid);
		border: 1px solid var(--border);
		border-radius: 14px;
		box-shadow: var(--shadowLight);

		z-index: 9999;
		pointer-events: auto;
	}

	/* User avatar button */
	.user-button {
		width: 34px;
		height: 34px;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;

		background: var(--hover);
		border: none;
		border-radius: 50%;
		cursor: pointer;

		transition:
			background 0.15s ease,
			box-shadow 0.15s ease,
			transform 0.06s ease;
	}

	.user-button:hover {
		background: var(--accent);
	}

	.user-button:hover .user-initials {
		color: white;
	}

	.user-button.open {
		background: var(--accent);
		box-shadow: 0 0 0 2px var(--panelSolid), 0 0 0 4px var(--accent);
	}

	.user-button.open .user-initials {
		color: white;
	}

	.user-button:active {
		transform: scale(0.95);
	}

	.user-initials {
		font-size: 12px;
		font-weight: 700;
		color: var(--textMuted);
		user-select: none;
		letter-spacing: 0.02em;
		transition: color 0.15s ease;
	}

	/* User dropdown */
	.user-menu-container {
		position: relative;
	}

	.user-menu {
		position: absolute;
		top: calc(100% + 16px);
		left: 0;
		width: 260px;

		background: var(--panelSolid);
		border: 1px solid var(--border);
		border-radius: 12px;
		box-shadow: var(--shadowLight);

		padding: 6px;
		z-index: 10000;
	}

	/* Account header */
	.menu-header {
		display: flex;
		align-items: center;
		gap: 12px;
		padding: 10px 10px;
	}

	.menu-header-avatar {
		width: 36px;
		height: 36px;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;

		background: var(--accent);
		border-radius: 50%;
	}

	.menu-header-initials {
		font-size: 13px;
		font-weight: 700;
		color: white;
		user-select: none;
	}

	.menu-header-info {
		display: flex;
		flex-direction: column;
		gap: 2px;
		min-width: 0;
	}

	.menu-header-name {
		font-size: 14px;
		font-weight: 600;
		color: var(--text);
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.menu-header-meta {
		display: flex;
		align-items: center;
		gap: 6px;
		font-size: 12px;
	}

	.meta-plan {
		font-weight: 600;
		text-transform: capitalize;
		color: var(--accent);
	}

	.meta-credits {
		color: var(--textMuted);
	}

	/* Menu sections */
	.menu-section {
		display: flex;
		flex-direction: column;
		gap: 1px;
	}

	.menu-label {
		font-size: 11px;
		font-weight: 600;
		color: var(--textMuted);
		padding: 6px 10px 4px;
		text-transform: uppercase;
		letter-spacing: 0.5px;
	}

	.menu-divider {
		height: 1px;
		background: var(--border);
		margin: 4px 6px;
	}

	/* Menu items */
	.menu-item {
		width: 100%;
		padding: 8px 10px;
		font-size: 13px;
		color: var(--text);
		text-align: left;
		background: transparent;
		border: none;
		border-radius: 8px;
		cursor: pointer;
		display: flex;
		align-items: center;
		gap: 10px;
		transition: background 0.1s ease;
	}

	.menu-item:hover {
		background: var(--hover);
	}

	.menu-item.active {
		color: var(--accent);
	}

	.menu-item-icon {
		display: flex;
		align-items: center;
		flex-shrink: 0;
		color: var(--textMuted);
	}

	.menu-item.active .menu-item-icon {
		color: var(--accent);
	}

	.menu-item-icon :global(svg) {
		width: 16px;
		height: 16px;
	}

	.menu-item-text {
		flex: 1;
	}

	.menu-item-check {
		font-size: 11px;
		font-weight: 600;
		color: var(--accent);
		text-transform: uppercase;
		letter-spacing: 0.3px;
	}

	.menu-item-danger {
		color: var(--textMuted);
	}

	.menu-item-danger:hover {
		color: #ef4444;
		background: rgba(239, 68, 68, 0.06);
	}

	.menu-item-danger:hover .menu-item-icon {
		color: #ef4444;
	}

	/* Grid style chips */
	.grid-options {
		display: flex;
		gap: 4px;
		padding: 4px 10px 6px;
		flex-wrap: wrap;
	}

	.grid-option {
		padding: 4px 10px;
		font-size: 12px;
		font-weight: 500;
		color: var(--textMuted);
		background: var(--hover);
		border: 1px solid transparent;
		border-radius: 6px;
		cursor: pointer;
		transition:
			background 0.1s ease,
			color 0.1s ease,
			border-color 0.1s ease;
	}

	.grid-option:hover {
		color: var(--text);
		background: var(--hover);
		border-color: var(--border);
	}

	.grid-option.active {
		color: var(--accent);
		background: transparent;
		border-color: var(--accent);
		font-weight: 600;
	}
</style>
